'use client';

import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { getAttribution } from '@/lib/analytics/attribution';
import { useRouter, usePathname } from 'next/navigation';
import Image from 'next/image';
import { Globe, MessageSquare } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/Button';
import styles from './AuditHero.module.css';
import { ProductPreview } from './ProductPreview';
import { AuditProgressNotification } from './AuditProgressNotification';

type InputMode = 'chat' | 'url';

const RESEARCH_LOGOS = [
  { src: '/logos/nielsen-logo-white.svg', alt: 'Nielsen Norman Group', width: 80 },
  { src: '/logos/mit-logo-white.svg', alt: 'MIT', width: 70 },
  { src: '/logos/ibm-logo-white.svg', alt: 'IBM Research', width: 50 },
];

export function AuditHero() {
  const t = useTranslations('audit.hero');
  const router = useRouter();
  const pathname = usePathname();
  const locale = pathname.split('/')[1] || 'en';

  const [activeTab, setActiveTab] = useState<InputMode>('url');
  const [domain, setDomain] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);
  const [auditUrl, setAuditUrl] = useState<string | null>(null);

  const fullUrl = domain.trim() ? `https://${domain.trim()}` : '';

  // Cancel handler - abort the current audit
  const handleCancel = useCallback(() => {
    setIsLoading(false);
    setAuditUrl(null);
    setError(null);
  }, []);

  // Rotate logos every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLogoIndex((prev) => (prev + 1) % RESEARCH_LOGOS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleUrlSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!domain.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);
    setAuditUrl(fullUrl);

    try {
      const response = await fetch('/api/audit/free', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: fullUrl, attribution: getAttribution() }),
      });

      const data = await response.json();

      if (data.success && data.reportId) {
        // Redirect to results page
        router.push(`/${locale}/audit/${data.reportId}${data.accessToken ? `?t=${encodeURIComponent(data.accessToken)}` : ''}`);
      } else {
        setError(
          data.code === 'UNRESOLVABLE_HOST' ? t('unresolvableDomain') : data.error || 'Audit failed'
        );
        setAuditUrl(null);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setAuditUrl(null);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <section className={styles.hero}>
      <div className="audit-container">
        {/* Title */}
        <h1 className={styles.title}>
          <span className={styles.titleLine}>{t('title.line1')}</span>
          <span className={styles.titleLine}>{t('title.line2')}</span>
        </h1>

        {/* Subtitle */}
        <p className={styles.subtitle}>
          {t('subtitle.line1')}
          <br />
          {t('subtitle.line2')}
        </p>

        {/* Trust Badge with Rotating Research Logos */}
        <div className={styles.trustBadge}>
          <span>{t('trustBadge')}</span>
          <div className={styles.logoRotator}>
            {RESEARCH_LOGOS.map((logo, index) => (
              <Image
                key={logo.src}
                src={logo.src}
                alt={logo.alt}
                width={logo.width}
                height={14}
                className={`${styles.trustLogo} ${index === currentLogoIndex ? styles.active : ''}`}
              />
            ))}
          </div>
        </div>

        {/* Dual Input (Chat OR URL) - Chat first per approved design */}
        <div className={styles.dualInput}>
          {/* Tabs - Chat first - Complete ARIA tab pattern */}
          <div className={styles.inputTabs} role="tablist" aria-label={t('tabs.ariaLabel')}>
            <button
              id="tab-chat"
              role="tab"
              aria-selected={activeTab === 'chat'}
              aria-controls="panel-chat"
              tabIndex={activeTab === 'chat' ? 0 : -1}
              className={`${styles.inputTab} ${activeTab === 'chat' ? styles.active : ''}`}
              onClick={() => setActiveTab('chat')}
            >
              <MessageSquare size={16} aria-hidden="true" />
              {t('tabs.chat')}
            </button>
            <button
              id="tab-url"
              role="tab"
              aria-selected={activeTab === 'url'}
              aria-controls="panel-url"
              tabIndex={activeTab === 'url' ? 0 : -1}
              className={`${styles.inputTab} ${activeTab === 'url' ? styles.active : ''}`}
              onClick={() => setActiveTab('url')}
            >
              <Globe size={16} aria-hidden="true" />
              {t('tabs.url')}
            </button>
          </div>

          {/* Chat Panel - Redirect to dedicated chat page */}
          <div
            id="panel-chat"
            role="tabpanel"
            aria-labelledby="tab-chat"
            hidden={activeTab !== 'chat'}
            className={`${styles.inputPanel} ${styles.chatPanel} ${activeTab === 'chat' ? styles.active : ''}`}
          >
            <div className={styles.chatTeaser}>
              <MessageSquare size={48} className={styles.chatIcon} aria-hidden="true" />
              <h3 className={styles.chatTeaserTitle}>Chat with the UX Expert</h3>
              <p className={styles.chatTeaserText}>
                Ask questions about UX/UI principles, get design advice, or analyze your website
                with our UX Expert, built on 167 research-backed Audit Criteria.
              </p>
              <Button
                onClick={() => router.push(`/${locale}/chat`)}
                size="lg"
              >
                Start Chatting
              </Button>
            </div>
          </div>

          {/* URL Panel */}
          <form
            id="panel-url"
            role="tabpanel"
            aria-labelledby="tab-url"
            hidden={activeTab !== 'url'}
            className={`${styles.inputPanel} ${activeTab === 'url' ? styles.active : ''}`}
            onSubmit={handleUrlSubmit}
          >
            <div className={styles.inputRow}>
              <label htmlFor="url-input" className={styles.visuallyHidden}>
                {t('urlInput.label')}
              </label>
              <div className={styles.inputFieldWrapper}>
                <Globe size={18} className={styles.inputIcon} />
                <span className={styles.inputPrefix}>https://</span>
                <input
                  id="url-input"
                  type="text"
                  className={styles.inputField}
                  placeholder="example.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value.replace(/^https?:\/\//, ''))}
                  required
                  autoComplete="off"
                  disabled={isLoading}
                />
              </div>
              <Button type="submit" size="lg" disabled={isLoading || !domain.trim()}>
                {isLoading ? t('urlInput.loading') : t('urlInput.button')}
              </Button>
            </div>

            {/* Progress Notification */}
            <AuditProgressNotification
              isVisible={isLoading}
              url={auditUrl || undefined}
              tier="free"
              error={error || undefined}
              locale={locale}
              onCancel={handleCancel}
            />

            {!isLoading && error && <p className={styles.errorMessage} role="alert">{error}</p>}
            {!isLoading && <p className={styles.inputHint}>{t('urlInput.hint')}</p>}
          </form>
        </div>

        {/* Social Proof - matching approved design */}
        <div className={styles.socialProof}>
          <div className={styles.proofStat}>
            <span className={styles.proofNumber}>{t('proof.citations.value')}</span>
            <span className={styles.proofLabel}>{t('proof.citations.label')}</span>
          </div>
          <div className={styles.proofDivider} />
          <div className={styles.proofStat}>
            <span className={styles.proofNumber}>{t('proof.principles.value')}</span>
            <span className={styles.proofLabel}>{t('proof.principles.label')}</span>
          </div>
          <div className={styles.proofDivider} />
          <div className={styles.proofStat}>
            <span className={styles.proofNumber}>{t('proof.speed.value')}</span>
            <span className={styles.proofLabel}>{t('proof.speed.label')}</span>
          </div>
        </div>
      </div>

      {/* Product Preview - Shows example of audit results */}
      <div className="audit-container">
        <ProductPreview />
      </div>
    </section>
  );
}
